import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { motion } from "motion/react";
import { useAuth, useMe } from "../auth.tsx";
import { useApi } from "../api.ts";
import { useToast } from "../ui/index.tsx";
import { bySaleRank } from "./_shared.tsx";
import { Stage, rise, stagger, useCalm } from "./member/kit.tsx";
import { Hero } from "./licenses/Hero.tsx";
import { Library } from "./licenses/Library.tsx";
import { Redeem } from "./licenses/Redeem.tsx";
import { BuyDrawer, Store } from "./licenses/Store.tsx";
import { History } from "./licenses/History.tsx";
import { Gifts } from "./licenses/Gifts.tsx";
import type { Game, Redemption } from "../../shared/types.ts";

/** After Stripe redirects back, the webhook may land a few seconds later, so poll /me briefly. */
function useCheckoutReturn() {
  const [params, setParams] = useSearchParams();
  const { refresh } = useAuth();
  const toast = useToast();
  useEffect(() => {
    if (params.get("checkout") !== "success") return;
    toast("Payment received. Your access will appear in a moment.", "success");
    setParams({}, { replace: true });
    const timers = [2000, 5000, 10000, 20000].map((ms) => setTimeout(() => void refresh(), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
}

export default function Licenses() {
  const me = useMe();
  const calm = useCalm();
  useCheckoutReturn();
  const redemptions = useApi<Redemption[]>("/me/redemptions");
  const gamesRes = useApi<Game[]>("/games");
  const [buyId, setBuyId] = useState<string | null>(null);

  const games = useMemo(() => new Map((gamesRes.data ?? []).map((g) => [g.id, g])), [gamesRes.data]);
  const owned = useMemo(() => new Map(me.entitlements.map((e) => [e.game_id, e])), [me.entitlements]);
  // Buyable games first, then coming-soon ones (their tile opens a notify form). Discontinued titles aren't offered.
  const forSale = useMemo(() => (gamesRes.data ?? [])
    .filter((g) => g.active !== false && g.status !== "discontinued" && (g.purchasable ? g.pricing_tiers.length > 0 : true))
    .sort(bySaleRank), [gamesRes.data]);

  const closeBuy = useCallback(() => setBuyId(null), []);
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "start" });
  const goRedeem = () => { jump("redeem"); setTimeout(() => document.getElementById("redeem-key")?.focus({ preventScroll: true }), calm ? 0 : 400); };
  const goStore = () => jump("store");

  return (
    <Stage>
      <motion.div variants={stagger(0.1)} initial="hidden" animate="show" className="space-y-6 overflow-x-clip">
        <motion.div variants={rise}><Hero list={me.entitlements} onRedeem={goRedeem} onBrowse={goStore} /></motion.div>

        <div className="flex flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,1fr)_24rem] xl:items-start">
          <div className="contents xl:flex xl:min-w-0 xl:flex-col xl:gap-6">
            <motion.div variants={rise} className="order-1 min-w-0 xl:order-none">
              <Library list={me.entitlements} games={games} onExtend={setBuyId} onRedeem={goRedeem} onBrowse={goStore} />
            </motion.div>
            <motion.div variants={rise} className="order-3 min-w-0 xl:order-none">
              <Store games={forSale} owned={owned} onPick={setBuyId} />
            </motion.div>
          </div>
          <div className="contents xl:flex xl:min-w-0 xl:flex-col xl:gap-6">
            <motion.div variants={rise} className="order-2 xl:order-none"><Redeem onRedeemed={redemptions.reload} /></motion.div>
            <motion.div variants={rise} className="order-2 xl:order-none"><Gifts /></motion.div>
            <motion.div variants={rise} className="order-4 min-w-0 xl:order-none">
              <History redemptions={redemptions} entitlements={me.entitlements} games={games} />
            </motion.div>
          </div>
        </div>
      </motion.div>

      <BuyDrawer game={buyId ? games.get(buyId) ?? null : null} current={buyId ? owned.get(buyId) : undefined} onClose={closeBuy} />
    </Stage>
  );
}
